import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import * as api from '../api.ts';
import { ReverifyModal } from '../reverify.tsx';
import { Button, Field, Finding, Section, Select, Spinner, Status } from '../ui.tsx';

/**
 * Getting the organisation confirmed (migration 0150,
 * docs/owner-verification.md §"The confirmation request").
 *
 * Written as what it means for the people in the building, never as a badge:
 * an unconfirmed organisation is not broken, its live alerts reach the people
 * who joined. The one thing confirmation changes is that a live alert can also
 * reach people on the list who have not accepted an invitation yet.
 *
 * The pending copy promises no turnaround: a person decides, and nothing
 * guarantees when.
 */

const COUNTRIES: { value: string; label: string }[] = [
  { value: 'AU', label: 'Australia' },
  { value: 'NZ', label: 'New Zealand' },
  { value: 'GB', label: 'United Kingdom' },
  { value: 'US', label: 'United States' },
  { value: 'other', label: 'Somewhere else' },
];

/** The business-number kinds a country can use, in the order offered. */
function kindsFor(country: string): { value: api.BusinessNumberKind; label: string }[] {
  switch (country) {
    case 'AU':
      return [
        { value: 'abn', label: 'ABN' },
        { value: 'acn', label: 'ACN' },
      ];
    case 'NZ':
      return [{ value: 'nzbn', label: 'NZBN' }];
    case 'GB':
      return [{ value: 'uk_company', label: 'Companies House number' }];
    case 'US':
      return [{ value: 'ein', label: 'EIN' }];
    default:
      return [{ value: 'other', label: 'Business registration number' }];
  }
}

export function OrgConfirmation({
  orgId,
  orgName,
  onInvite,
}: {
  orgId: string;
  orgName: string;
  onInvite: () => void;
}) {
  const v = useQuery({
    queryKey: ['org-verification', orgId],
    queryFn: () => api.getOrgVerification(orgId),
  });
  const [asking, setAsking] = useState(false);

  if (v.isLoading || !v.data) {
    return (
      <Section title="Confirmation">
        {v.isError ? <Finding>{api.errorMessage(v.error)}</Finding> : <Spinner />}
      </Section>
    );
  }
  const s = v.data;

  if (s.status === 'confirmed') {
    return (
      <Section title="Confirmation">
        <Status tone="safe">Confirmed organisation</Status>
        <p className="mt-3 max-w-prose text-[14px] text-[var(--ink-2)]">
          Live alerts can reach everyone on your list
          {s.confirmedAt
            ? `. Confirmed ${new Date(s.confirmedAt).toLocaleDateString(undefined, {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}.`
            : '.'}
        </p>
      </Section>
    );
  }

  if (s.status === 'pending') {
    return (
      <Section title="Confirmation">
        <h3 className="text-[17px] font-semibold text-[var(--ink)]">
          We&rsquo;re checking your details
        </h3>
        <p className="mt-2 max-w-prose text-[14px] text-[var(--ink-2)]">
          A person at Duress looks at every request. We&rsquo;ll email you when it&rsquo;s done.
          Meanwhile live alerts go to people who accepted your invite and anyone who follows this
          place — invite your team so they&rsquo;re covered.
        </p>
        {s.request ? (
          <p className="mt-3 text-[13px] text-[var(--ink-3)]">
            Asked as {s.request.legalName} · {s.request.website}
          </p>
        ) : null}
        <div className="mt-4">
          <Button onClick={onInvite}>Invite people</Button>
        </div>
      </Section>
    );
  }

  return (
    <Section title="Confirmation">
      {s.status === 'rejected' ? (
        <>
          <h3 className="text-[17px] font-semibold text-[var(--ink)]">
            We couldn&rsquo;t confirm {orgName} yet
          </h3>
          {s.rejection?.reason ? (
            <div className="mt-3 max-w-prose">
              <Finding tone="neutral">{s.rejection.reason}</Finding>
            </div>
          ) : null}
          <p className="mt-2 max-w-prose text-[14px] text-[var(--ink-2)]">
            Nothing you have set up has stopped working. Correct the details and ask again.
          </p>
        </>
      ) : (
        <>
          <h3 className="text-[17px] font-semibold text-[var(--ink)]">
            Alerts reach people who&rsquo;ve joined
          </h3>
          <p className="mt-2 max-w-prose text-[14px] text-[var(--ink-2)]">
            Until we&rsquo;ve confirmed your organisation, live alerts go to people who accepted
            your invite and anyone who follows this place. Invite your team so they&rsquo;re
            covered.
          </p>
        </>
      )}

      {asking ? (
        <ConfirmationForm
          orgId={orgId}
          {...(s.request ? { previous: s.request } : {})}
          onDone={() => setAsking(false)}
        />
      ) : (
        <div className="mt-4 flex flex-wrap gap-3">
          {s.canSubmit ? (
            <Button variant="primary" onClick={() => setAsking(true)}>
              {s.status === 'rejected' ? 'Ask again' : 'Get confirmed'}
            </Button>
          ) : (
            <p className="text-[13px] text-[var(--ink-3)]">
              Your organisation&rsquo;s owner or an administrator can ask to get it confirmed.
            </p>
          )}
          <Button onClick={onInvite}>Invite people</Button>
        </div>
      )}
    </Section>
  );
}

function ConfirmationForm({
  orgId,
  previous,
  onDone,
}: {
  orgId: string;
  previous?: NonNullable<api.OrgVerification['request']>;
  onDone: () => void;
}) {
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.getMe() });
  const knownCountry = (c: string | undefined) =>
    c && COUNTRIES.some((x) => x.value === c) ? c : c ? 'other' : 'AU';
  const [legalName, setLegalName] = useState(previous?.legalName ?? '');
  const [country, setCountry] = useState(knownCountry(previous?.countryCode));
  const [otherCountry, setOtherCountry] = useState(
    previous && knownCountry(previous.countryCode) === 'other' ? previous.countryCode : '',
  );
  const [kind, setKind] = useState<api.BusinessNumberKind>(
    (previous?.businessNumberKind as api.BusinessNumberKind | undefined) ?? 'abn',
  );
  const [number, setNumber] = useState(previous?.businessNumber ?? '');
  const [website, setWebsite] = useState(previous?.website ?? '');
  const [role, setRole] = useState(previous?.role ?? '');
  const [reverify, setReverify] = useState(false);

  const kinds = kindsFor(country);
  const effectiveKind = kinds.some((k) => k.value === kind) ? kind : kinds[0]!.value;
  const countryCode = country === 'other' ? otherCountry.trim().toUpperCase() : country;

  const submit = useMutation({
    mutationFn: () =>
      api.submitOrgVerification(orgId, {
        legalName: legalName.trim(),
        countryCode,
        businessNumberKind: effectiveKind,
        businessNumber: number.trim(),
        website: website.trim(),
        role: role.trim(),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['org-verification', orgId] });
      onDone();
    },
    onError: (e) => {
      if (e instanceof api.ApiError && e.detail.code === 'stale_session') setReverify(true);
    },
  });

  const ready =
    legalName.trim().length >= 2 &&
    /^[A-Z]{2}$/.test(countryCode) &&
    number.trim().length >= 2 &&
    website.trim().length >= 3 &&
    role.trim().length >= 2;

  const stale =
    submit.error instanceof api.ApiError && submit.error.detail.code === 'stale_session';

  return (
    <div className="mt-4 flex max-w-[480px] flex-col gap-4">
      {reverify && me.data?.email ? (
        <ReverifyModal
          email={me.data.email}
          onClose={() => setReverify(false)}
          onVerified={() => {
            setReverify(false);
            submit.mutate();
          }}
        />
      ) : null}
      <p className="text-[14px] text-[var(--ink-2)]">
        Tell us who the organisation is. A person at Duress checks it against the public business
        register and your website. We see the domain of the address you signed in with
        {me.data?.email ? ` (${me.data.email.split('@')[1]})` : ''}, so an address at your
        organisation&rsquo;s own domain helps.
      </p>
      <Field
        label="Legal business name"
        value={legalName}
        onChange={setLegalName}
        placeholder="As it appears on the business register"
      />
      <Select label="Country" value={country} onChange={setCountry} options={COUNTRIES} />
      {country === 'other' ? (
        <Field
          label="Country code"
          value={otherCountry}
          onChange={setOtherCountry}
          placeholder="Two letters, like SG"
        />
      ) : null}
      {kinds.length > 1 ? (
        <Select
          label="Business number"
          value={effectiveKind}
          onChange={(v) => setKind(v as api.BusinessNumberKind)}
          options={kinds}
        />
      ) : null}
      <Field
        label={kinds.length > 1 ? 'Number' : kinds[0]!.label}
        value={number}
        onChange={setNumber}
        inputMode={effectiveKind === 'other' || effectiveKind === 'uk_company' ? 'text' : 'numeric'}
      />
      <Field
        label="Website"
        value={website}
        onChange={setWebsite}
        placeholder="example.com.au"
        autoComplete="url"
      />
      <Field
        label="Your role"
        value={role}
        onChange={setRole}
        placeholder="Facilities manager"
        autoComplete="organization-title"
      />
      {submit.isError && !stale ? <Finding>{api.errorMessage(submit.error)}</Finding> : null}
      <div className="flex gap-3">
        <Button
          variant="primary"
          disabled={!ready || submit.isPending}
          onClick={() => submit.mutate()}
        >
          {submit.isPending ? 'Sending…' : 'Send for checking'}
        </Button>
        <Button variant="quiet" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
